// ============================================================================
// roomTree.js — szobák fa-sorrendbe rendezése (2026-09-28).
//
// A szobák beágyazhatók (rooms/{id}.parentRoomId) - egy szoba lehet egy
// másik "alszobája", tetszőleges mélységben. Ez a modul NEM dönt semmiről,
// csak megjelenítési sorrendet ad: minden szoba után KÖZVETLENÜL a saját
// gyerekei következnek (mélységi bejárással), a `melyseg` a behúzáshoz kell.
//
// Egy alszoba akkor számít "gyereknek", ha a szülője IS szerepel a kapott
// `rooms` listában - ha nem (mert pl. ennek a felhasználónak nincs hozzá-
// férése a szülőhöz, vagy az törölve lett), a szoba egyszerűen főszobaként
// jelenik meg. Ez sosem hibázik: minden bemeneti szoba pontosan egyszer
// szerepel a kimenetben, körkörös hivatkozás esetén is (védekezésül).
// ============================================================================

/**
 * @param {Array<{roomId: string, parentRoomId?: string|null}>} rooms
 * @returns {Array<{room: object, melyseg: number}>}
 */
export function szobaFaSorrend(rooms) {
  const byId = new Map(rooms.map((r) => [r.roomId, r]));
  const gyerekei = new Map();
  const gyokerek = [];

  for (const r of rooms) {
    const szuloLathato =
      r.parentRoomId && r.parentRoomId !== r.roomId && byId.has(r.parentRoomId);
    if (szuloLathato) {
      if (!gyerekei.has(r.parentRoomId)) gyerekei.set(r.parentRoomId, []);
      gyerekei.get(r.parentRoomId).push(r);
    } else {
      gyokerek.push(r);
    }
  }

  // Testvér-sorrend (2026-10-20, v125): az admin által beállított `sortOrder` szerint, a számozatlanok a számozottak UTÁN, egymás közt az eredeti sorrendben.
  const eredetiHely = new Map(rooms.map((r, i) => [r.roomId, i]));
  const sorrendben = (lista) => lista.slice().sort((a, b) => {
    const x = Number.isFinite(a.sortOrder) ? a.sortOrder : Infinity;
    const y = Number.isFinite(b.sortOrder) ? b.sortOrder : Infinity;
    return x === y ? eredetiHely.get(a.roomId) - eredetiHely.get(b.roomId) : x - y;
  });
  const rendezettGyokerek = sorrendben(gyokerek);
  for (const [szulo, lista] of gyerekei) gyerekei.set(szulo, sorrendben(lista));

  const eredmeny = [];
  const bejart = new Set();
  const bejar = (room, melyseg) => {
    if (bejart.has(room.roomId)) return; // védelem véletlen kör ellen
    bejart.add(room.roomId);
    eredmeny.push({ room, melyseg });
    for (const gyerek of gyerekei.get(room.roomId) || []) bejar(gyerek, melyseg + 1);
  };
  for (const gy of rendezettGyokerek) bejar(gy, 0);
  // Ha valamiért (adathiba, pl. körkörös lánc) egy szoba kimaradt volna,
  // gyökérszintként a végén mégis megjelenik - egy szoba sem tűnhet el.
  for (const r of rooms) if (!bejart.has(r.roomId)) bejar(r, 0);

  return eredmeny;
}

/**
 * A szobák az ÉLŐ lista (amit a tagok is látnak) sorrendjében: az admin-lista (azonosító szerint rendezett) alap sorrendje így megegyezik a tagokéval,
 * ezért a fel / le gombok azt a sorrendet mozgatják, amit látnak. Az élő listában nem szereplők a végére kerülnek.
 */
export function alapSorrend(rooms, eloSzobak) {
  const hely = new Map((eloSzobak || []).map((r, i) => [r.roomId, i]));
  return (rooms || []).map((r, i) => [r, i]).sort((a, b) => {
    const x = hely.has(a[0].roomId) ? hely.get(a[0].roomId) : Infinity;
    const y = hely.has(b[0].roomId) ? hely.get(b[0].roomId) : Infinity;
    return x === y ? a[1] - b[1] : x - y;
  }).map((p) => p[0]);
}

/**
 * Egy szoba testvérei (azonos szülő, nem törlés alatti) MEGJELENÉSI sorrendben, és a szoba helye közöttük - a fel / le gombokhoz.
 * @returns {{testverek: object[], index: number}}
 */
export function testverCsoport(rooms, room) {
  const szulo = room && room.parentRoomId ? room.parentRoomId : null;
  const lathato = new Set((rooms || []).map((r) => r.roomId));
  const csoport = szobaFaSorrend(rooms || []).map((x) => x.room).filter((r) => {
    const sz = r.parentRoomId && r.parentRoomId !== r.roomId && lathato.has(r.parentRoomId) ? r.parentRoomId : null;
    return sz === (szulo && lathato.has(szulo) ? szulo : null) && !r.deleting;
  });
  return { testverek: csoport, index: csoport.findIndex((r) => r.roomId === (room && room.roomId)) };
}

/**
 * Az azonosítók halmaza, amik NEM választhatók szülőnek egy adott szoba
 * szerkesztésekor: önmaga, és minden (bármilyen mélységű) leszármazottja -
 * ezek elfogadása nyilvánvaló kört zárna be. A szerver ezt véglegesen úgyis
 * ellenőrzi; ez csak a választó listát tisztítja meg előre.
 *
 * @param {Array<{roomId: string, parentRoomId?: string|null}>} rooms
 * @param {string} roomId
 * @returns {Set<string>}
 */
export function nemValaszthatoSzuloId(rooms, roomId) {
  const tiltott = new Set([roomId]);
  // Rögzített pontig ismételjük: minden körben azok kerülnek be, akiknek a
  // szülője már tiltott - ez pontosan a leszármazottakat gyűjti össze,
  // a beágyazás mélységétől függetlenül.
  let valtozott = true;
  while (valtozott) {
    valtozott = false;
    for (const r of rooms) {
      if (!tiltott.has(r.roomId) && r.parentRoomId && tiltott.has(r.parentRoomId)) {
        tiltott.add(r.roomId);
        valtozott = true;
      }
    }
  }
  return tiltott;
}

// ---------------------------------------------------------------------------
// 2026-10-04: a hierarchia MINDENHOL egységesen jelenjen meg (tagi kérésre).
// ---------------------------------------------------------------------------

/**
 * Egy szoba felirata egy <select> opciójában: a mélység szerint behúzva, az
 * alszoba elé "↳" jellel (az <option> nem tud valódi behúzást/formázást, ezért
 * szóköz + jel). EGY HELYEN van, mert korábban a három szobaválasztó három
 * külön másolatot tartott fenn - és a tagok szobaválasztója le is maradt
 * (lapos lista maradt).
 */
export function szobaOpcioFelirat(melyseg, nev) {
  return "\u00A0\u00A0".repeat(melyseg) + (melyseg > 0 ? "\u21B3 " : "") + nev;
}

/**
 * Egy szoba ősei a főszobától a közvetlen szülőig (a szoba maga nélkül). Csak
 * azokat az őst adja vissza, amelyek szerepelnek a kapott `rooms` listában -
 * ugyanaz a szabály, mint a szobaFaSorrend-nél: egy ismeretlen (pl. a tag
 * számára nem elérhető) szülőn túl nem látunk. Körkörös adatnál és túl mély
 * láncnál sem fut végtelenül.
 *
 * @returns {object[]} a szobák, gyökértől a szülőig
 */
export function szuloUtvonal(rooms, room) {
  if (!room) return [];
  const byId = new Map(rooms.map((r) => [r.roomId, r]));
  const lanc = [];
  const lattuk = new Set([room.roomId]);
  let aktualis = room;
  for (let i = 0; i < 16; i++) {
    const szulo = aktualis.parentRoomId && byId.get(aktualis.parentRoomId);
    if (!szulo || lattuk.has(szulo.roomId)) break;
    lattuk.add(szulo.roomId);
    lanc.unshift(szulo);
    aktualis = szulo;
  }
  return lanc;
}

/** Azok az azonosítók, amelyeknek van (a listában is szereplő) alszobájuk - a főszobák kiemeléséhez. */
export function szuloSzobaIdk(rooms) {
  const ids = new Set(rooms.map((r) => r.roomId));
  const szulok = new Set();
  for (const r of rooms) {
    if (r.parentRoomId && r.parentRoomId !== r.roomId && ids.has(r.parentRoomId)) szulok.add(r.parentRoomId);
  }
  return szulok;
}
